import React, {
  useMemo, useState, forwardRef
} from 'react';
import { Form, Title } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldImageGalleryUpload, FieldToggle
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormikForwardRef } from 'hooks'
import { useDebounce } from 'react-use'
import { linkRegex, linkRegexOptional } from 'services/constants'
import { generateKey } from 'services/generators'
import { useParams } from 'react-router-dom'
import SliderCell from './SliderCell'

const min = new Date();
min.setFullYear(min.getFullYear() - 1);
const max = new Date();
max.setFullYear(max.getFullYear() + 1);
const ItemSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  href: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'),
  image: Yup.object({
    url: Yup.string().required('Campo de imagem obrigatório'),
    alt: Yup.string(),
    title: Yup.string()
  }).required('Campo de imagem obrigatório'),
  startDate: Yup.date().required('Campo obrigatório').min(min, `Data de inicio deve ser maior que ${min}`).max(max, `Data deve ser menor que ${max}`),
  endDate: Yup.date().min(Yup.ref('startDate'),
    'Data de fim deve ser posterior à de inicio')
    .default(() => new Date())
    .max(max, `Data deve ser menor que ${max}`)
});


// eslint-disable-next-line react/prop-types
const CampusForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const {
      title, description, href, alt, image, startDate, endDate, hasEndDate
    } = initialValues
    const hasEndDateTemp = hasEndDate == null ? false : (hasEndDate !== 'undefined' && !hasEndDate)
    return {
      title,
      description,
      href,
      startDate,
      endDate,
      hasEndDateTemp,
      image: {
        url: image,
        alt,
        title: '',
      }
    }
  }, [initialValues])

  const formToModuleSchema = (values) => {
    const {
      title, description, href, image: { url, alt }, startDate, endDate, hasEndDateTemp
    } = values
    const hasEndDate = !hasEndDateTemp
    return {
      title,
      description,
      href,
      image: url,
      alt,
      startDate,
      endDate,
      hasEndDate
    }
  }

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >

      <Form>
        <Field
          title="Imagem"
          name="image"
          component={FieldImageGalleryUpload}
        />
        <Field
          title="Título"
          name="title"
          info="Título do destaque"
          component={FieldCustom}
        />

        <Field
          title="Descrição"
          name="description"
          component={FieldCustom}
        />
        <Field
          title="Link"
          name="href"
          component={FieldCustom}
        />
        <Field
          title="Data de inicio"
          name="startDate"
          component={FieldCustom}
          type="datetime-local"
        />
        <Field
          title="data de fim igual a do inicio"
          name="hasEndDateTemp"
          component={FieldToggle}
        />
        <Field
          title="Data de fim"
          name="endDate"
          component={FieldCustom}
          type="datetime-local"
        />
      </Form>
    </Formik>
  )
})


const Schema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  description: Yup.string(),
  href: Yup.string().matches(linkRegexOptional, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'),
  buttonLink: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'),
  hasImageSection: Yup.bool(),
  hasLink: Yup.bool(),
  hasButton: Yup.bool(),
  imageCover: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
});


const SubWebEventsEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)
  const { lang } = useParams()

  const initialValues = useMemo(() => {
    const {
      title, hasLink, hasImageSection, imageCover, alt, data, href, ...values
    } = initialProps;

    let list = [];

    data.map((item) => {
      const { cards } = item
      list = [...list, ...cards]
      return item
    })
    const description = lang === 'pt' ? 'Ver programa completo' : 'See program';

    return {
      ...values,
      title,
      hasLink,
      description,
      hasImageSection,
      image: { url: imageCover, alt },
      href,
      list
    };
  }, [initialProps]);


  const handleFormChange = newValues => {
    const {
      image: { url: imageCover, alt }, list, href, buttonLink, hasImageSection, ...values
    } = newValues

    let data = [];


    for (let i = 0; i < list.length; i += 1) {
      if (i + 2 <= list.length && ((i + 2) % 5 % 4) === 0) {
        data = [...data, { type: 'left', cards: [list[i], list[i + 1]] }]
        i += 1;
      } else if (i + 2 <= list.length && ((i + 2) % 5 % 2) === 0) {
        data = [...data, { type: 'right', cards: [list[i], list[i + 1]] }]
        i += 1;
      } else {
        data = [...data, { type: 'extend', cards: [list[i]] }]
      }
    }
    const hrefTmp = (href !== 'undefined' && href !== '') ? href : undefined;
    const newProps = {
      ...values,
      imageCover,
      hasLink: hrefTmp !== null && hrefTmp !== undefined && hrefTmp !== '',
      hasImageSection,
      alt,
      href: hrefTmp,
      buttonLink,
      data
    }

    const isValid = Schema.isValidSync(newProps)

    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200,
  [editorProps]
  )


  const columns = useMemo(() => ([
    {
      accessor: 'image',
      Cell: SliderCell,
    },
    {
      accessor: 'title',
    }
  ]), [])


  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
      enableReinitialize
    >
      <Form>
        <Title as="h3">Título</Title>
        <Field
          title="Título"
          name="title"
          info="Uma frase para secção"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Link Titulo"
          name="href"
          component={FieldCustom}
          maxLetters={200}
        />
        <Title as="h3">Programa completo</Title>
        <Field
          title="Mostrar botão"
          name="hasButton"
          component={FieldToggle}
        />
        <Field
          title="Link Programa"
          name="buttonLink"
          component={FieldCustom}
          maxLetters={200}
        />
        <Title as="h3">Imagem de capa</Title>
        <Field
          title="Mostrar"
          name="hasImageSection"
          component={FieldToggle}
        />
        <Field
          title="Imagem"
          name="image"
          component={FieldImageGalleryUpload}
        />

        <Title as="h3">Eventos</Title>
        <Field
          title="Programa"
          name="list"
          component={FieldArrayDndFormik}
          extra={{ hasLink: initialProps.type === 'withLink' }}
          columns={columns}
          rowKey={(item) => generateKey(item.title)}
          ItemForm={CampusForm}
          shouldAddOnTop
          shouldHideHeaders
        />
      </Form>
    </Formik>
  );
};

SubWebEventsEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    title: PropTypes.string,
    imageCover: PropTypes.string,
  }),
};

SubWebEventsEditor.defaultProps = {
  initialProps: {}
};

export default SubWebEventsEditor;
